import { sanitizeCountryCode } from "@expert/common-ui";
import { Stack } from "@mantine/core";
import { type JSX, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";

import { usePartnerConfig, usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import { useAgentSdk, useAgentStore, useCallbackState, type VoiceTask } from "@/sdk";
import { classNames, isNullish, useFeatureFlag } from "@/shared-utils";

import { CallControls } from "./CallControls";
import classes from "./CallControlsManager.module.css";
import { CallbackCallBar } from "./CallDetails";
import { ConferencePanel } from "./ConferencePanel";
import { useControlsStore } from "./controls.store";
import { IdleNoCallControls } from "./NoCallControls";
import { WhisperPill } from "./utility-components";
import { WrappingCallControls } from "./WrappingCallControls";

export function CallControlsManager({ voiceTask }: { voiceTask: VoiceTask | null }): JSX.Element {
    const callbackState = useCallbackState();
    const { isConference, conferencePanelOpen } = useControlsStore();
    const agentActivity = useAgentStore(useShallow((state) => state.activity));
    const { outboundCall, callControlsEnabled, callbackEnabled } = usePartnerFeatureConfigured();
    const { offlineAgentActivity } = useAgentSdk();
    const {
        technicalIssues,
        callbackPendingAgentActivity = offlineAgentActivity,
        wrappingAgentActivity,
        features: {
            countrySelectEnabled = false,
            clientEnabledCountries = ["US"],
            isVdnTransferInputEnabled = false,
            defaultCountry = "US",
            isSegmentedTransferMenuEnabled = false,
            isInternationalPhoneEnabled = false,
        },
    } = usePartnerConfig();

    const { data: isNewCallOutboundDisabled = false } = useFeatureFlag("EWP-disable-new-call-outbound");

    const isOutboundEnabled =
        !isNewCallOutboundDisabled &&
        (outboundCall || (technicalIssues?.enableOutboundCalls && agentActivity === technicalIssues.agentActivity));

    const sanitizedDefaultCountry = sanitizeCountryCode(defaultCountry);
    const sanitizedClientEnabledCountries = clientEnabledCountries.map(sanitizeCountryCode);

    const hasCallbackWaiting = useMemo(() => {
        return (
            !isNullish(callbackState?.callbackDelay) &&
            callbackState.scheduledFor &&
            callbackState.callbackType === "CallbackNow"
        );
    }, [callbackState]);

    const noActiveCall = !voiceTask && !hasCallbackWaiting;
    const inWrapping = agentActivity === wrappingAgentActivity || voiceTask?.status === "wrapping";
    const activeConference = voiceTask && isConference && conferencePanelOpen;

    return (
        <Stack className={classes.controlsContainer}>
            {callControlsEnabled ? (
                <Stack gap={0} w="100%" justify="center" align="center">
                    {voiceTask?.whisper ? <WhisperPill whisper={voiceTask.whisper} /> : null}
                    <Stack
                        className={classNames(
                            noActiveCall && !isOutboundEnabled && !inWrapping ? null : classes.controlsWrapper,
                            !noActiveCall ? classes.controlsOnCall : null,
                            activeConference ? classes.noBottomLeftRadius : null,
                        )}
                        data-testid="controls-wrapper"
                    >
                        {voiceTask ? (
                            <CallControls
                                voiceTask={voiceTask}
                                countrySelectEnabled={countrySelectEnabled}
                                clientEnabledCountries={sanitizedClientEnabledCountries}
                                isVdnTransferInputEnabled={isVdnTransferInputEnabled}
                                defaultCountry={sanitizedDefaultCountry}
                                isSegmentedTransferMenuEnabled={isSegmentedTransferMenuEnabled}
                                isInternationalPhoneEnabled={isInternationalPhoneEnabled}
                            />
                        ) : null}
                        {isOutboundEnabled && noActiveCall && !inWrapping ? (
                            <IdleNoCallControls
                                isInternationalPhoneEnabled={isInternationalPhoneEnabled}
                                defaultCountry={sanitizedDefaultCountry}
                            />
                        ) : null}
                        {callbackEnabled &&
                        !voiceTask &&
                        agentActivity === callbackPendingAgentActivity &&
                        hasCallbackWaiting ? (
                            <CallbackCallBar />
                        ) : null}
                        {noActiveCall && inWrapping ? <WrappingCallControls /> : null}
                    </Stack>

                    <Stack className={classes.dropdownControlsWrapper}>
                        {activeConference && !inWrapping ? <ConferencePanel task={voiceTask} /> : null}
                    </Stack>
                </Stack>
            ) : (
                <Stack
                    className={classNames(noActiveCall && isOutboundEnabled ? classes.controlsWrapper : null)}
                    data-testid="controls-wrapper"
                >
                    {isOutboundEnabled && noActiveCall && !inWrapping ? (
                        <IdleNoCallControls
                            isInternationalPhoneEnabled={isInternationalPhoneEnabled}
                            defaultCountry={sanitizedDefaultCountry}
                        />
                    ) : null}
                </Stack>
            )}
        </Stack>
    );
}
